import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import Payment from './payment';
import { Navbar } from './Navbar';
import { motion } from 'framer-motion';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const api = process.env.REACT_APP_API_URL;

function PayScreen() {
  const location = useLocation();
  const navigate = useNavigate();

  const { data, jobDescriptionFile, jobDescriptionText, resumeFile } = location.state || {};

  const [, setPaymentSuccess] = useState(false);
  const [promo, setPromo] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [promoLeft, setPromoLeft] = useState(null); // {remaining, cap} once known

  // Show the live allowance up front, so nobody discovers "0 left" only after
  // typing the code.
  useEffect(() => {
    const token = Cookies.get('access_token');
    if (!token) return;
    axios.get(`${api}promo/status/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setPromoLeft(data))
      .catch(() => { /* count is a nicety; the redeem call is the authority */ });
  }, []);

  // A valid promo code covers this run entirely: the session is marked paid
  // server-side and the flow continues exactly as after a card payment.
  const redeemPromo = async () => {
    if (!promo.trim() || redeeming) return;
    setRedeeming(true);
    try {
      const sessionId = localStorage.getItem('sessionId');
      const { data: res } = await axios.post(`${api}promo/redeem/`,
        { session_id: sessionId, code: promo.trim() },
        { headers: { Authorization: `Bearer ${Cookies.get('access_token')}` } });
      const steps = JSON.parse(localStorage.getItem('completedSteps') || '[]');
      if (!steps.includes('pay')) {
        steps.push('pay');
        localStorage.setItem('completedSteps', JSON.stringify(steps));
      }
      if (typeof res.remaining === 'number') {
        setPromoLeft((prev) => ({ cap: prev?.cap, remaining: res.remaining }));
      }
      toast.success(res.remaining === 1
        ? 'Code applied. This run is free, and you have 1 free run left.'
        : `Code applied. This run is free, and you have ${res.remaining} free runs left.`);
      navigate('/fullScore', { state: { data, jobDescriptionFile, jobDescriptionText, resumeFile } });
    } catch (e) {
      // "Not valid" only when the server actually said so; other failures
      // (expired session, auth hiccup) get their own truthful message.
      toast.error(e?.response?.data?.message || e?.response?.data?.error
        || 'Could not apply the code. Please try again.');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="flex flex-col  min-h-screen p-6 md:p-16">
        <ToastContainer />

        {/* Header Section */}
        <motion.h1
          className="text-4xl font-bold text-gray-800 text-center"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Complete Your Payment
        </motion.h1>
        <motion.p
          className="text-lg text-gray-600 text-center mt-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          Unlock access to your detailed recommendations!
        </motion.p>

        {/* Payment Section */}
        <div className="flex justify-center mt-8">
          <motion.div
            className="bg-gradient-to-br  shadow-lg rounded-lg p-6 w-full md:w-1/2"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-lg font-semibold text-gray-700 text-center">
              Payment Plan
            </h2>
            <p className="text-base text-gray-600 text-center mt-4">
              Access AI-powered recommendations with just a few clicks.
            </p>

            {/* Pricing Box */}
            <div className="border border-gray-300 rounded-lg p-4 mt-4 text-center">
              <div className="text-3xl font-bold text-gray-800">
                $1.99 <span className="text-sm font-normal text-gray-500">per match</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Recommendations are accessible only after payment.
              </p>
              <hr className="my-4" />
              <ul className="list-disc text-left text-gray-700 ml-6 space-y-2">
                <li>Applicable for one run</li>
                <li>Access all missing keywords</li>
                <li>Get detailed recommendations</li>
              </ul>
            </div>

            <div className="mt-6 border border-gray-400 rounded-lg p-4">
              <Payment
                setPaymentSuccess={setPaymentSuccess}
                data={data}
                jobDescriptionFile={jobDescriptionFile}
                jobDescriptionText={jobDescriptionText}
                resumeFile={resumeFile}
              />
            </div>

            {/* Promo code: a valid code covers this run entirely */}
            <div className="mt-4 border border-dashed border-gray-300 rounded-lg p-4">
              <p className="text-sm font-semibold text-gray-700">Have a promo code?</p>
              <div className="flex gap-2 mt-2">
                <input
                  type="text"
                  value={promo}
                  onChange={(e) => setPromo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') redeemPromo(); }}
                  placeholder="Enter your code"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <button
                  onClick={redeemPromo}
                  disabled={!promo.trim() || redeeming}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-semibold hover:bg-gray-700 disabled:opacity-50 transition"
                >
                  {redeeming ? 'Checking…' : 'Apply'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                A valid code makes this run free. No card needed.
                {promoLeft && promoLeft.remaining > 0 && (
                  <span className="text-gray-500"> Your account has {promoLeft.remaining} promo {promoLeft.remaining === 1 ? 'run' : 'runs'} left.</span>
                )}
                {promoLeft && promoLeft.remaining === 0 && (
                  <span className="text-gray-500"> Your account has used all its promo runs.</span>
                )}
              </p>
            </div>

           
          </motion.div>
        </div>
      </div>
    </>
  );
  
}

export default PayScreen;
